import { createClient } from '@supabase/supabase-js';

/**
 * Supabase client singleton — the auth + data transport for QA Studio,
 * replacing the Hono custom-auth + api-fetch layer (ADR-012 · slot
 * slot_supabase_migration · SM.3 auth · SM.4 edge functions).
 *
 * Auth flows (signup / login / email verify / password reset / Google OAuth)
 * call `supabase.auth.*`. Edge-function calls (SM.4) attach the access token
 * read from `supabase.auth.getSession()`.
 *
 * `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY` are injected at build time —
 * `.env.local` for dev, the Netlify dashboard for prod. The anon key is a
 * public, RLS-gated key by design: safe to ship in the client bundle.
 *
 * `detectSessionInUrl` + `flowType: 'pkce'` let supabase-js transparently pick
 * up the session from email-verify / password-reset / OAuth redirect URLs, so
 * the auth screens do not parse tokens by hand.
 */

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !anonKey) {
  // Surface loudly. The fallbacks below keep `createClient` from throwing at
  // module load (it rejects empty url/key) — a thrown import would white-screen
  // the app and crash the test suite. A missing-env boot stays diagnosable;
  // the first real call fails with a clear Supabase error instead.
  console.error(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not set — auth ' +
      'and API calls will fail. Check .env.local (dev) or the Netlify dashboard (prod).',
  );
}

/**
 * Entry-URL markers captured at module load, BEFORE createClient() runs —
 * detectSessionInUrl consumes and strips the token fragment right after init.
 * `/reset-password` uses this to tell a fresh email link (hash carries
 * type=recovery|invite, or a PKCE ?code=) from plain navigation with a live
 * session. One-shot link rule · Genie 6 JUN 2026.
 */
export const entryUrlHadEmailLink: boolean =
  typeof window !== 'undefined' &&
  (/type=(recovery|invite)/.test(window.location.hash) ||
    /[?&](code|token_hash)=/.test(window.location.search));

export const supabase = createClient(
  url || 'http://localhost:54321',
  anonKey || 'anon-key-not-configured',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  },
);
